import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback, useEffect, Fragment } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl, defineMessages } from 'react-intl';
import type { MutationUpdaterFn } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import StarIcon from '@atlaskit/icon/core/star-unstarred';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getTemplateId } from '@confluence/template-utils/entry-points/templateUtils.ts';
import { StarSelectedIcon } from '@confluence/icons/entry-points/StarSelectedIcon.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { StarContainer } from './TemplateCardStyles.tsx';
import type {
	StarTemplateFavouriteStatusMutation as StarTemplateFavouriteStatusMutation$data,
	StarTemplateFavouriteStatusMutationVariables as StarTemplateFavouriteStatusMutation$variables,
} from './__types__/StarTemplateFavouriteStatusMutation.ts';

const i18n = defineMessages({
	star: {
		id: 'template-card.star.label',
		defaultMessage: 'Star {templateName}',
		description: 'String to use as the aria-label for the star',
	},
	starTooltip: {
		id: 'template-card.star.tooltip',
		defaultMessage: 'Star this template',
		description: 'String to use as the tooltip for the star',
	},
	filledStar: {
		id: 'template-card.filled-star.label',
		defaultMessage: 'Unstar {templateName}',
		description: 'String to use as the aria-label for the filled star',
	},
	filledStarTooltip: {
		id: 'template-card.filled-star.tooltip',
		defaultMessage: 'Unstar this template',
		description: 'String to use as the tooltip for the filled star',
	},
});

export type StarTemplate = {
	name: string | null;
	contentBlueprintId: string | null;
	templateId: string | null;
	isFavourite?: boolean | null; // remove the `?` when starring FF is removed
};

type Props = {
	template: StarTemplate;
	isDisabled?: boolean;
	onStarClick?: MutationUpdaterFn | undefined;
};

const StarComponent = injectIntl(
	({ isDisabled, template, onStarClick, intl }: Props & WrappedComponentProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const templateId = getTemplateId(template);
		const isFavourite = template.isFavourite;
		const [updateUserPreferences, { loading: isUserPreferencesLoading, error }] =
			useMutationInterop<
				StarTemplateFavouriteStatusMutation$data,
				StarTemplateFavouriteStatusMutation$variables
			>(graphql`
				mutation StarTemplateFavouriteStatusMutation(
					$isFavourite: Boolean!
					$templateEntityId: String!
				) {
					updateUserPreferences(
						userPreferences: {
							templateEntityFavouriteStatus: {
								isFavourite: $isFavourite
								templateEntityId: $templateEntityId
							}
						}
					) {
						favouriteTemplateEntityIds
					}
				}
			`);
		useEffect(() => {
			if (error) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'templateDrawer',
						action: 'error',
						actionSubject: 'button',
						actionSubjectId: 'templateStarClickedError',
						attributes: {
							isFavourite,
							templateId,
							errorMessage: error.message,
							starAction: isFavourite ? 'unstarring' : 'starring',
						},
					},
				}).fire();
			}
		}, [error, createAnalyticsEvent, templateId, isFavourite]);

		const star = useCallback(
			async (e: any) => {
				e.persist();
				e.stopPropagation();
				if (isUserPreferencesLoading || !onStarClick) {
					return;
				}
				try {
					await updateUserPreferences({
						variables: {
							isFavourite: true,
							templateEntityId: templateId,
						},
						update: onStarClick,
					});
				} catch {
					// error handled by useMutation result
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[
				updateUserPreferences,
				isUserPreferencesLoading,
				templateId,
				onStarClick,
				updateUserPreferences,
			],
		);

		const unstar = useCallback(
			async (e: any) => {
				e.persist();
				e.stopPropagation();
				if (isUserPreferencesLoading || !onStarClick) {
					return;
				}
				try {
					await updateUserPreferences({
						variables: {
							isFavourite: false,
							templateEntityId: templateId,
						},
						update: onStarClick,
					});
				} catch {
					// error handled by useMutation result
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[
				updateUserPreferences,
				isUserPreferencesLoading,
				templateId,
				onStarClick,
				updateUserPreferences,
			],
		);

		if (isFavourite) {
			return (
				<Fragment>
					{error ? <ErrorDisplay error={error} /> : null}
					<Tooltip
						key={0}
						content={isDisabled ? null : intl.formatMessage(i18n.filledStarTooltip)}
						hideTooltipOnClick
					>
						<StarContainer isVisible onClick={unstar} disabled={isDisabled}>
							<StarSelectedIcon
								label={intl.formatMessage(i18n.filledStar, {
									templateName: template.name,
								})}
								testId="template-card-filled-star"
							/>
						</StarContainer>
					</Tooltip>
				</Fragment>
			);
		}

		return (
			<Fragment>
				{error ? <ErrorDisplay error={error} /> : null}
				<Tooltip
					key={1}
					content={isDisabled ? null : intl.formatMessage(i18n.starTooltip)}
					hideTooltipOnClick
				>
					<StarContainer onClick={star} disabled={isDisabled}>
						<StarIcon
							spacing="spacious"
							label={intl.formatMessage(i18n.star, {
								templateName: template.name,
							})}
							testId="template-card-star"
							color={token('color.icon')}
						/>
					</StarContainer>
				</Tooltip>
			</Fragment>
		);
	},
);

export const Star: FC<Props> = (props) => {
	const { isLicensed } = useSessionData();
	return isLicensed ? <StarComponent {...props} /> : null;
};
